import React, { createContext, useCallback, useContext, useState } from 'react';

export interface SignupFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  avatar?: File;
  avatarUri?: string;
}

export interface SignupContextType {
  formData: SignupFormData;
  updateFormData: (data: Partial<SignupFormData>) => void;
  resetFormData: () => void;
  customerId?: string;
  setCustomerId: (id: string) => void;
}

const initialFormData: SignupFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
};

const SignupContext = createContext<SignupContextType | undefined>(undefined);

export interface SignupProviderProps {
  children: React.ReactNode;
}

export function SignupProvider({ children }: SignupProviderProps) {
  const [formData, setFormData] = useState<SignupFormData>(initialFormData);
  const [customerId, setCustomerId] = useState<string | undefined>();

  const updateFormData = useCallback((data: Partial<SignupFormData>) => {
    setFormData((prevData) => ({ ...prevData, ...data }));
  }, []);

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
    setCustomerId(undefined);
  }, []);

  const value: SignupContextType = {
    formData,
    updateFormData,
    resetFormData,
    customerId,
    setCustomerId,
  };

  return (
    <SignupContext.Provider value={value}>{children}</SignupContext.Provider>
  );
}

export function useSignupContext(): SignupContextType {
  const context = useContext(SignupContext);
  if (context === undefined) {
    throw new Error('useSignupContext must be used within a SignupProvider');
  }
  return context;
}
